// Staff org sessions (#10718 phase 2): the client half of entering and
// releasing a client organization's environment.
//
// The server is the source of truth (the session row); identity (`fp0052`)
// reports the acting org and the session. After an enter or release the
// store's `staffOrgSession` is set from the response so the notice and the
// "Current" pill move at once, and the identity query is invalidated so the
// acting org and everything hanging off it follow from the server.
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import { updateUserData } from "@/redux/userSlice";
import { CURRENT_USER_QUERY_KEY } from "./useCurrentUser";

/** The session as `fp0052` reports it (`staffOrgSession`). */
export interface StaffOrgSession {
  id: number;
  organizationId: string;
  organizationName: string;
  reason: string;
  /** ISO instant; the notice counts down to it on the viewer's clock. */
  expiresAt: string;
}

/** The session as the staff routes return it. */
interface StaffOrgSessionOut {
  id: number;
  organization_id: string;
  reason: string;
  expires_at: string;
}

export const SESSION_URL = `${BASE_API_URL_V2}/api/v2/staff/org-session`;

export const TTL_CHOICES: ReadonlyArray<{ minutes: number; label: string }> = [
  { minutes: 15, label: "15 minutes" },
  { minutes: 60, label: "1 hour" },
  { minutes: 240, label: "4 hours" },
  { minutes: 480, label: "8 hours" },
];
export const DEFAULT_TTL_MINUTES = 60;

/** Whole minutes until `expiresAt`, never negative. */
export function minutesLeft(expiresAt: string, now: Date = new Date()): number {
  const ms = new Date(expiresAt).getTime() - now.getTime();
  return Math.max(0, Math.floor(ms / 60_000));
}

export function useStaffOrgSession(): {
  enter: (
    target: { id: string; name: string },
    reason: string,
    ttlMinutes: number
  ) => Promise<boolean>;
  release: () => Promise<boolean>;
} {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const refreshIdentity = useCallback(
    () => queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY }),
    [queryClient]
  );

  const enter = useCallback(
    async (
      target: { id: string; name: string },
      reason: string,
      ttlMinutes: number
    ): Promise<boolean> => {
      const response = await fpFetch(SESSION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          organization_id: target.id,
          reason,
          ttl_minutes: ttlMinutes,
        }),
      });
      if (!response?.ok) return false;
      const entered = (await response.json()) as StaffOrgSessionOut;
      const session: StaffOrgSession = {
        id: entered.id,
        organizationId: entered.organization_id,
        organizationName: target.name,
        reason: entered.reason,
        expiresAt: entered.expires_at,
      };
      dispatch(updateUserData({ staffOrgSession: session }));
      await refreshIdentity();
      return true;
    },
    [dispatch, refreshIdentity]
  );

  const release = useCallback(async (): Promise<boolean> => {
    const response = await fpFetch(SESSION_URL, { method: "DELETE" });
    // 404 means there was nothing to release; the end state is the same.
    if (!response || (!response.ok && response.status !== 404)) return false;
    dispatch(updateUserData({ staffOrgSession: null }));
    await refreshIdentity();
    return true;
  }, [dispatch, refreshIdentity]);

  return { enter, release };
}
